/**
 * SectionShell - frame for secondary areas (My Account, Settings) that matches
 * the main practice layout: floating pill header on the cream canvas, a white
 * section menu card on the left and the rounded content panel on the right.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ArrowLeft, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { isHiddenHref } from "@/lib/hidden-features";
import { FloatingHeader } from "@/components/layout/PracticeLayout";
import { ShellPageTransition, type ShellNavItem, type ShellNavSection } from "@/components/layout/shell";
import { WorkbenchFloatingDock, WorkbenchTabBarSlot } from "@/components/layout/workbench";

interface SectionShellProps {
  /** Small coral heading above the menu, e.g. "My account". */
  title: string;
  sections: ShellNavSection[];
  isItemActive: (pathname: string, item: ShellNavItem) => boolean;
  /** Line under the title, e.g. the clinic name. */
  subtitle?: string;
  /** Show a filter box above the menu (long menus such as Settings). */
  searchable?: boolean;
  /** Extra card under the menu (desktop only). */
  aside?: ReactNode;
}

export function SectionShell({ title, subtitle, sections, isItemActive, searchable, aside }: SectionShellProps) {
  const { user } = useAuth();
  const location = useLocation();
  const visible = sections
    .map((section) => ({ ...section, items: section.items.filter((item) => !isHiddenHref(item.href)) }))
    .filter((section) => section.items.length > 0);
  const allItems = visible.flatMap((section) => section.items);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = q
    ? visible
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => `${item.label} ${section.label}`.toLowerCase().includes(q)),
        }))
        .filter((section) => section.items.length > 0)
    : visible;

  // Keep the active pill in view on the mobile menu.
  const mobileRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    mobileRef.current?.querySelector<HTMLElement>("[data-active=true]")?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [location.pathname]);

  return (
    <div className="app-shell relative flex h-screen flex-col overflow-hidden bg-[var(--canvas)]">
      <FloatingHeader user={user} />

      <div className="flex min-h-0 flex-1 gap-2 px-3 pb-3 sm:px-4">
        {/* Section menu */}
        <aside className="hidden w-64 shrink-0 flex-col gap-2 overflow-y-auto scrollbar-none lg:flex">
          <nav className="rounded-[24px] bg-card p-3 shadow-[0_1px_2px_rgba(0,58,67,0.05)]" aria-label={title}>
            <Link
              to="/dashboard"
              className="mb-2 flex h-9 items-center gap-2 rounded-full px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to app
            </Link>
            <div className="px-3 pb-2 pt-2">
              <p className="text-lg font-semibold tracking-tight text-foreground">{title}</p>
              {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
            </div>
            {searchable && (
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Find in ${title.toLowerCase()}`}
                  aria-label={`Find in ${title.toLowerCase()}`}
                  className="h-9 w-full rounded-full border-0 bg-[#f8f3eb] pl-9 pr-8 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-[#004952]/20 dark:bg-muted"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
            {shown.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground">Nothing matches "{query}".</p>}
            {shown.map((section, index) => (
              <div key={section.label} className={cn(index > 0 && "mt-2 border-t border-border/60 pt-2")}>
                {shown.length > 1 && (
                  <p className="px-3 pb-1 pt-1 text-xs font-medium text-muted-foreground">{section.label}</p>
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const active = isItemActive(location.pathname, item);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "flex h-9 items-center gap-3 rounded-full px-3 text-sm font-medium transition-colors",
                          active
                            ? "bg-[#003a43] text-white dark:bg-primary dark:text-primary-foreground"
                            : "text-foreground/75 hover:bg-[#f8f3eb] hover:text-foreground dark:hover:bg-muted",
                        )}
                      >
                        <item.icon className={cn("h-4 w-4 shrink-0", active ? "text-[#ff8d6e]" : "text-muted-foreground")} />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
          {aside}
        </aside>

        {/* Content panel */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[24px] bg-background shadow-[0_8px_30px_-12px_rgba(0,58,67,0.25)] ring-1 ring-[#d6cdbb] dark:ring-border">
          {/* Section menu (mobile / tablet) */}
          <div ref={mobileRef} className="flex shrink-0 gap-1.5 overflow-x-auto border-b border-border px-3 py-2.5 scrollbar-none lg:hidden">
            {allItems.map((item) => {
              const active = isItemActive(location.pathname, item);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-active={active}
                  className={cn(
                    "flex h-9 shrink-0 items-center gap-2 rounded-full px-3.5 text-sm font-medium",
                    active ? "bg-[#003a43] text-white" : "bg-[#f8f3eb] text-foreground/75 dark:bg-muted",
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </div>

          <WorkbenchTabBarSlot />
          <main className="relative min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-[1400px] p-4 pb-16 sm:p-6">
              <ShellPageTransition>
                <Outlet />
              </ShellPageTransition>
            </div>
          </main>
          <WorkbenchFloatingDock />
        </div>
      </div>
    </div>
  );
}
